import { isEqual } from 'lodash';
import Quagga from 'quagga';
import { Observable, Subject, Subscriber } from 'rxjs';
import { bufferCount, distinctUntilChanged, map } from 'rxjs/operators';

export interface Result {
    box: any;
    boxes: any;
    codeResult: {
        code: string;
    };
}

export type Reader = string | { config: Record<'supplements', string[]>; format: string };

export class CameraScanner extends Observable<string> {
    private track: MediaStreamTrack;
    // tslint:disable-next-line:variable-name
    private _torch = false;
    private subscriber: Subscriber<string>;
    private queue$ = new Subject<Result>();
    private readers: Reader[];

    constructor(readers: Reader[]) {
        super(o => {
            this.subscriber = o;
        });

        this.readers = readers;
        this.queue$
            .pipe(
                bufferCount(2),
                distinctUntilChanged(
                    // we use !isEqual instead of isEqual because we want to emit only if previous and next are equal.
                    // We inverse the operator behavior.
                    (previous: Result[], next: Result[]) => !isEqual(previous, next),
                ),
                map(([result]: Result[]) => result.codeResult.code),
            )
            .subscribe(code => this.subscriber.next(code));
    }

    get torch() {
        return this._torch;
    }

    public async toggleTorch() {
        try {
            await this.applyTrackConstraints({
                advanced: [{ torch: !this._torch } as any],
            });
            this._torch = !this._torch;
        } catch (error) {
            // do nothing
        }
    }

    public start() {
        Quagga.onProcessed(this.onProcessed);
        Quagga.onDetected(this.onDetected);

        return new Promise((resolve, reject) => {
            Quagga.init(
                {
                    decoder: {
                        readers: this.readers,
                    },
                    frequency: 10,
                    inputStream: {
                        constraints: {
                            facingMode: 'environment',
                            height: { max: window.innerHeight },
                            width: { max: window.innerWidth },
                        },
                        type: 'LiveStream',
                    },
                    locate: true,
                    locator: {
                        halfSample: false,
                    },
                    numOfWorkers: 2,
                },
                async (error: Error) => {
                    if (error) {
                        reject(error);
                    }

                    this.track = Quagga.CameraAccess.getActiveTrack();
                    await Quagga.start();
                    resolve();
                },
            );
        });
    }

    public async stop() {
        Quagga.offProcessed(this.onProcessed);
        Quagga.offDetected(this.onDetected);

        if (this._torch) {
            await this.toggleTorch();
        }
        return await Quagga.stop();
    }

    private async applyTrackConstraints(constraints: Record<string, any>) {
        if (this.track && typeof this.track.applyConstraints === 'function') {
            await this.track.applyConstraints(constraints);
            return true;
        }

        throw new Error('applyConstraint is not compatible with this platform');
    }

    private onDetected = (result: Result) => this.queue$.next(result);

    private onProcessed = result => {
        if (!result) {
            return;
        }

        const drawingCtx = Quagga.canvas.ctx.overlay;
        const drawingCanvas = Quagga.canvas.dom.overlay;

        if (result.boxes) {
            drawingCtx.clearRect(
                0,
                0,
                parseInt(drawingCanvas.getAttribute('width'), 10),
                parseInt(drawingCanvas.getAttribute('height'), 10),
            );
            result.boxes
                .filter(box => {
                    return box !== result.box;
                })
                .forEach(box => {
                    Quagga.ImageDebug.drawPath(box, { x: 0, y: 1 }, drawingCtx, {
                        color: 'green',
                        lineWidth: 2,
                    });
                });
        }

        if (result.box) {
            Quagga.ImageDebug.drawPath(result.box, { x: 0, y: 1 }, drawingCtx, {
                color: '#00F',
                lineWidth: 2,
            });
        }

        if (result.codeResult && result.codeResult.code) {
            Quagga.ImageDebug.drawPath(result.line, { x: 'x', y: 'y' }, drawingCtx, {
                color: 'red',
                lineWidth: 3,
            });
        }
    };
}
